import { pick } from 'lodash-es';

import { getTheme } from '../services/themes';

import getDefaultFields from './graphics-fields';
import { setFields } from './graphics-utils';

const TRACKING_FIELDS = [
  'trackingLinkId',
  'trackingLinkRid',
  'trackingLink',
  'trackingLinkMetadataRid',
];

/**
 * Copy tracking link fields from a source (element/settings) onto target fields.
 * @param {Object} target - Fields to update.
 * @param {Object} [source] - Fields to read tracking links from.
 * @returns {Object} Target fields.
 */
const applyTrackingFields = (target, source = {}) => {
  const next = target;

  TRACKING_FIELDS.forEach((field) => {
    const value = source[field];

    if (value !== undefined && value !== null && value !== '') {
      next[field] = value;
    }
  });

  return next;
};

/**
 * Propagate the tracking link selected on a panel's QR code to the timeline item.
 * @param {Object} opts.post - Timeline item.
 * @param {Object} opts.panels - Map containing all panel data by ID.
 */
export const shortenPanelUrls = async (opts = {}) => {
  const {
    post,
    panels,
  } = opts;

  // If provided post isn't even a panel, then do nothing.
  if (post?.layout !== 'panel') { return; }

  const panel = panels[post._id];
  if (!panel) { return; }

  const fields = { ...panel.settings, ...(post.fields || {}) };

  if (!fields.showQrCode || !fields.trackingLinkRid) {
    return;
  }

  post.fields = post.fields || { visible: true };
  applyTrackingFields(post.fields, fields);

  post.fields.qrCodeUrl = fields.qrCodeUrl || post.fields.qrCodeUrl || '';
};

/**
 * Propagate tracking links from a graphic template's QR elements to the
 * timeline item. Legacy short URL generation is no longer used.
 * @param {Object} opts.post - Timeline item.
 * @param {Object} opts.templates - Map containing all graphic template data by ID.
 * @param {Object} [opts.themes] - Map containing all theme data by ID.
 */
export const shortenGraphicUrls = async (opts = {}) => {
  const {
    post,
    templates,
    themes = {},
  } = opts;

  if (post?.layout !== 'graphic') { return; }

  const template = templates[post.template_id];
  if (!template) { return; }

  const processElements = (parentEl) => {
    const { elements } = parentEl;

    if (!elements?.length) {
      return Promise.resolve();
    }

    return Promise.all(
      elements.map(async (element) => {
        // Handle slideshow layers separately.
        if (element.type === 'slideshow') {
          let fields = post.fields?.[element.id];

          // Slideshows need fields before we can set tracking links on `entries`.
          if (!fields) {
            // This will mutate `post.fields` for us...
            setFields(post, [element]);
            fields = post.fields?.[element.id];
          }

          fields?.entries?.forEach((slide, slideIdx) => {
            slide.fields.forEach((layer, idx) => {
              if (layer.type !== 'qrcode' || !layer.trackingLinkRid) {
                return;
              }

              fields.entries[slideIdx].fields[idx] = {
                ...applyTrackingFields({}, layer),
                // Persisted overrides for this slide's QR win over the template.
                ...(fields.entries[slideIdx].fields[idx] || {}),
              };

              post.fields[layer.id] = {
                ...applyTrackingFields({}, layer),
                ...(post.fields[layer.id] || {}),
              };
            });
          });
        }

        if (element.elements?.length && element.type !== 'slideshow') {
          await processElements(element);
        }

        if (element.type === 'qrcode') {
          // Mapped QR codes get their URL from a data source.
          if (element.data?.provider) {
            return;
          }

          const existing = post.fields?.[element.id] || {};
          const trackingFields = applyTrackingFields({}, element);

          if (!trackingFields.trackingLinkRid && !element.permalink && !existing.permalink) {
            return;
          }

          post.fields = post.fields || {};

          post.fields[element.id] = {
            // Defaults
            ...getDefaultFields('qrcode'),
            ...pick(element, Object.keys(getDefaultFields('qrcode'))),

            // Tracking Link values from the template, used as a fallback when
            // the timeline item has no persisted override.
            ...trackingFields,

            // Existing fields win over the template so a tracking link selected
            // in Producer isn't clobbered on hydration/refresh.
            ...existing,
          };
        }

        // Handle social-display frames with QR codes.
        if (element.type === 'social-display' || element.isSocialDisplay === true) {
          const fields = post.fields?.[element.id] || {};
          let source = fields;

          // Fall back to the display's theme defaults when nothing is overridden.
          if (!fields.trackingLinkRid && !fields.showQrCode && !fields.permalink && element.displayId) {
            let theme = themes[element.displayId];

            if (!theme) {
              try {
                theme = await getTheme(element.displayId);

                if (theme) {
                  themes[element.displayId] = theme;
                }
              } catch (err) {
                console.warn('Failed to fetch theme for social display:', element.displayId, err);
              }
            }

            if (theme?.settings?.showQrCode && theme.settings.trackingLinkRid) {
              post.fields = post.fields || {};
              post.fields[element.id] = {
                ...post.fields[element.id],
                showQrCode: theme.settings.showQrCode,
                permalink: theme.settings.permalink,
                ...applyTrackingFields({}, theme.settings),
              };

              source = post.fields[element.id];
            }
          }

          if (source.trackingLinkRid) {
            post.fields = post.fields || {};
            post.fields[element.id] = applyTrackingFields({ ...post.fields[element.id] }, source);
          }
        }
      }),
    );
  };

  await processElements(template);
};

/**
 * Propagate the tracking link selected on a theme's QR code to the timeline item.
 * @param {Object} opts.post - Timeline item.
 * @param {Object} opts.themes - Map containing all theme data by ID.
 */
export const shortenThemeUrls = async (opts = {}) => {
  const {
    post,
    themes,
  } = opts;

  // If provided post isn't a theme layout, then do nothing.
  if (!['grid', 'socialburst', 'waterfall', 'carousel', 'isolated'].includes(post?.layout)) { return; }

  const theme = themes[post._id];
  if (!theme) { return; }

  const fields = { ...theme.settings, ...(post.fields || {}) };

  if (!fields.showQrCode || !fields.trackingLinkRid) {
    return;
  }

  post.fields = post.fields || { visible: true };
  applyTrackingFields(post.fields, fields);

  post.fields.permalink = fields.permalink || post.fields.permalink || '';
};
